
import React, { useState } from 'react';
import { Book, ArrowLeft, Layers, Brain, Flame, Coffee, Baby, FlaskRound, Timer, Smartphone, BookOpen, Wand2, Sparkles, GraduationCap, ExternalLink, Search, Info } from 'lucide-react';
import { CORES_CROMOTERAPIA, TEMPOS_APLICACAO, TRATAMENTOS_PRATICOS } from '../constants';

interface Props {
  onBack: () => void;
}

type Tab = 'TREINAMENTO' | 'EMERGENCIAS' | 'ENCICLOPEDIA' | 'DOSAGEM' | 'CORES' | 'AVANCADO';

const Library: React.FC<Props> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<Tab>('TREINAMENTO');
  const [searchTerm, setSearchTerm] = useState('');

  const tabs = [
    { id: 'TREINAMENTO', label: 'Mentalização', icon: Brain },
    { id: 'ENCICLOPEDIA', label: 'Protocolos Práticos', icon: BookOpen },
    { id: 'EMERGENCIAS', label: 'Urgências', icon: Flame },
    { id: 'DOSAGEM', label: 'Dosagens', icon: Baby },
    { id: 'CORES', label: 'Cores', icon: Layers },
    { id: 'AVANCADO', label: 'Avançado', icon: FlaskRound },
  ];

  const filteredConditions = TRATAMENTOS_PRATICOS.filter(t => 
    t.Condição.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t["Cores Sugeridas"].toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full animate-fade-in pb-20">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={onBack} className="p-4 rounded-2xl bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-200 hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors">
          <ArrowLeft size={28} />
        </button>
        <div>
          <h2 className="text-3xl font-bold text-chroma-text flex items-center gap-3">
            <Book size={32} className="text-chroma-accent" />
            Guia de Bolso
          </h2>
          <p className="text-base text-chroma-muted font-bold tracking-wide">Metodologia João Caputo</p>
        </div>
      </div>

      {/* Navegação de Abas Otimizada */}
      <div className="flex gap-3 mb-10 overflow-x-auto no-scrollbar pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button 
              key={tab.id} 
              onClick={() => { setActiveTab(tab.id as Tab); setSearchTerm(''); }}
              className={`flex-none w-32 sm:flex-1 flex flex-col items-center justify-center gap-2 p-4 rounded-2xl transition-all text-center border-2 shadow-sm h-28 snap-start ${
                isActive 
                  ? 'bg-chroma-accent border-chroma-accent text-white shadow-lg scale-[1.02] z-10' 
                  : 'bg-chroma-card border-chroma-border text-chroma-muted hover:border-chroma-accent/50 hover:bg-purple-50/50 dark:hover:bg-white/5'
              }`}
            >
              <Icon size={isActive ? 28 : 24} className={`${isActive ? 'text-white' : 'text-chroma-accent'} transition-all`} />
              <span className={`text-[10px] font-black uppercase tracking-widest leading-tight ${isActive ? 'text-white' : 'text-chroma-text'}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      <div className="space-y-10 mb-20">
        {activeTab === 'TREINAMENTO' && (
          <div className="space-y-8 animate-fade-in">
             <div className="bg-gradient-to-br from-chroma-accent/10 to-purple-600/10 border border-chroma-accent/20 rounded-3xl p-10">
                <h3 className="text-2xl font-bold text-chroma-text mb-8 flex items-center gap-3">
                   <Wand2 className="text-chroma-accent" size={28} /> Essência: Mentalização
                </h3>
                <p className="text-xl leading-loose mb-10 text-chroma-text font-light tracking-wide">
                  As lâmpadas LED são apenas **indutores**. O que realmente cura é a sua capacidade de projetar mentalmente a frequência luminosa.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white/50 dark:bg-black/40 p-10 rounded-3xl border border-purple-100 dark:border-purple-900/50 shadow-sm">
                    <h4 className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase mb-6 tracking-[0.3em]">Técnica de Varredura</h4>
                    <p className="text-lg leading-relaxed text-chroma-text font-light tracking-wide">Visualize a cor "varrendo" a estrutura física, como se o órgão estivesse iluminado.</p>
                  </div>
                  <div className="bg-white/50 dark:bg-black/40 p-10 rounded-3xl border border-purple-100 dark:border-purple-900/50 shadow-sm">
                    <h4 className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase mb-6 tracking-[0.3em]">Higiene Emocional</h4>
                    <p className="text-lg leading-relaxed text-chroma-text font-light tracking-wide">Não sofra com o assistido. Ofereça consolo e luz, mas mantenha seu equilíbrio para não "afogar" junto.</p>
                  </div>
                </div>
             </div>
          </div>
        )}

        {activeTab === 'ENCICLOPEDIA' && (
          <div className="space-y-8 animate-fade-in">
             <div className="relative">
                <Search className="absolute left-5 top-5 text-purple-500" size={24} />
                <input 
                  type="text" 
                  placeholder="Pesquisar condição (ex: Enxaqueca, Gastrite, Acne...)" 
                  className="w-full bg-chroma-card border border-chroma-border rounded-3xl py-5 pl-16 pr-6 text-lg outline-none focus:border-chroma-accent shadow-lg"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredConditions.map((item, idx) => (
                  <div key={idx} className="bg-chroma-card border border-chroma-border p-8 rounded-[2.5rem] shadow-sm hover:border-chroma-accent/30 transition-all flex flex-col gap-4">
                    <div className="flex justify-between items-start">
                      <h4 className="text-2xl font-bold text-chroma-text">{item.Condição}</h4>
                      <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Metodologia Original</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {item["Cores Sugeridas"].split(',').map((c, i) => (
                        <span key={i} className="px-4 py-1.5 bg-black/5 dark:bg-white/5 border border-purple-200 dark:border-white/10 rounded-full text-xs font-bold text-chroma-muted">
                          {c.trim()}
                        </span>
                      ))}
                    </div>
                    <div className="space-y-3 mt-4">
                       <p className="text-sm font-light leading-relaxed"><strong className="font-bold text-chroma-accent">Aplicação:</strong> {item["Método/Aplicação"]}</p>
                       <p className="text-sm font-light leading-relaxed"><strong className="font-bold text-chroma-accent">Tempo:</strong> {item["Duração/Temporização"]}</p>
                       {item.Observações && (
                         <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-2xl flex gap-3 italic text-xs text-chroma-muted">
                            <Info size={16} className="shrink-0" />
                            {item.Observações}
                         </div>
                       )}
                    </div>
                  </div>
                ))}
             </div>
          </div>
        )}

        {activeTab === 'EMERGENCIAS' && (
          <div className="space-y-10 animate-fade-in">
             <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-3xl p-10">
                <h3 className="text-2xl font-bold text-chroma-text mb-10 flex items-center gap-3"><Flame className="text-red-500" size={28} /> Queimaduras</h3>
                <div className="space-y-8">
                   <div className="bg-white/60 dark:bg-black/40 p-10 rounded-3xl border border-red-100 dark:border-red-900/30 shadow-sm">
                      <h4 className="text-[10px] font-black text-red-600 dark:text-red-400 uppercase mb-4 tracking-[0.3em]">1º Grau (Leve)</h4>
                      <p className="text-xl font-light tracking-wide">Verde (30s) + Azul (30s) + Aura Azul.</p>
                   </div>
                   <div className="bg-white/60 dark:bg-black/40 p-10 rounded-3xl border border-red-100 dark:border-red-900/30 shadow-sm">
                      <h4 className="text-[10px] font-black text-red-600 dark:text-red-400 uppercase mb-4 tracking-[0.3em]">2º Grau</h4>
                      <div className="text-lg leading-loose space-y-4 font-light tracking-wide">
                         <p><strong className="font-bold">Dia 1:</strong> Verde (30s) + Azul (30s), até 3x ao dia.</p>
                         <p><strong className="font-bold">Próximos:</strong> Verde (30s) &rarr; Violeta (30s) &rarr; Azul (30s).</p>
                         <p><strong className="font-bold">Recuperação:</strong> Azul Geral (30s) + Amarelo (30s) para regeneração.</p>
                      </div>
                   </div>
                </div>
             </div>
          </div>
        )}

        {activeTab === 'DOSAGEM' && (
          <div className="space-y-8 animate-fade-in">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-chroma-text mb-8 flex items-center gap-3">
                <Baby className="text-emerald-500" size={28} /> Dosagens por Idade
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {TEMPOS_APLICACAO.map((t, i) => (
                  <div key={i} className="p-8 bg-white/50 dark:bg-black/40 rounded-3xl border border-emerald-100 dark:border-emerald-900/30 flex justify-between items-center">
                    <div>
                       <p className="text-[10px] font-black uppercase text-emerald-600 tracking-widest">{t.min} a {t.max} anos</p>
                       <p className="text-3xl font-black text-chroma-text mt-1">{t.tempoBase}</p>
                    </div>
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-600">
                       <Timer size={24} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'CORES' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
            {CORES_CROMOTERAPIA.map((c, i) => (
              <div key={i} className="bg-chroma-card border border-chroma-border p-8 rounded-[2.5rem] shadow-sm flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl shadow-lg border-4 border-white dark:border-black" style={{ backgroundColor: c.hex }}></div>
                  <div>
                     <h4 className="font-black text-xl uppercase tracking-tighter">{c.cor}</h4>
                     <p className="text-[9px] text-chroma-muted font-bold tracking-widest uppercase">{c.onda}</p>
                  </div>
                </div>
                <p className="text-sm text-chroma-text font-light leading-relaxed flex-1 italic">"{c.efeitos}"</p>
                <div className="pt-4 border-t border-purple-100 dark:border-white/5">
                   <p className="text-[10px] font-black text-chroma-accent uppercase tracking-widest mb-2">Aplicações:</p>
                   <div className="flex flex-wrap gap-1">
                      {c.aplicabilidades.slice(0, 4).map((a, j) => (
                        <span key={j} className="text-[9px] bg-purple-100 dark:bg-white/5 px-2 py-0.5 rounded uppercase font-bold text-chroma-muted">{a}</span>
                      ))}
                   </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'AVANCADO' && (
          <div className="space-y-8 animate-fade-in">
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-chroma-text mb-8 flex items-center gap-3">
                <FlaskRound className="text-purple-500" size={28} /> Princípios de Apometria
              </h3>
              <p className="text-xl leading-loose text-chroma-text font-light tracking-wide italic">
                "A precisão mental é o motor da cura no plano etérico. O cromoterapeuta deve atuar como um engenheiro de luz, visualizando as estruturas orgânicas sendo regeneradas pela frequência exata."
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-10 bg-purple-50 dark:bg-purple-900/20 border-2 border-dashed border-purple-300 dark:border-purple-700 p-10 rounded-[2.5rem] text-center">
         <GraduationCap size={48} className="text-chroma-accent mx-auto mb-6" />
         <h3 className="text-2xl font-bold text-chroma-text mb-4">Formação Técnica Master</h3>
         <p className="text-chroma-muted mb-8 max-w-lg mx-auto font-medium">
           Deseja dominar a fundo todos os protocolos e a ciência da Apometria aplicada à Cromoterapia? Acesse o curso completo do Mestre João Caputo.
         </p>
         <a 
           href="https://caputoacademia.com.br/venda_cromoterapia" 
           target="_blank" 
           rel="noopener noreferrer"
           className="inline-flex items-center gap-3 bg-chroma-accent text-white px-10 py-4 rounded-2xl font-black shadow-lg hover:bg-purple-600 transition-all uppercase tracking-widest text-sm"
         >
           Acessar Conteúdo Completo
           <ExternalLink size={18} />
         </a>
      </div>
    </div>
  );
};

export default Library;
